/**
 * The usual shape of a brand's colour codes, read from the codes already in `tinte`, so the
 * search form can show an example under "Codice colore" and gently warn when the typed text
 * doesn't look like one (a chassis number, a colour name…). Pure: shared by the server, which
 * builds the profile, and the client form, which checks the text as the customer types.
 */
import { chiaveCodice } from '@/catalogo/prestashop/normalizza'
import { famigliaDaNome } from './famiglie'

/** A code's shape: `chiaveCodice` with digits as `9` and letters as `L` (`296/A` → `999L`). */
export const formaCodice = (codice: string): string =>
  chiaveCodice(codice).replace(/\d/g, '9').replace(/[A-Z]/g, 'L')

export type FormaTipica = {
  forma: string
  /** A real code of the brand with that shape, the most ordered one. */
  esempio: string
}

export type ProfiloCodice = {
  /** Display name of the brand ("Fiat"). */
  marca: string
  /** The shapes most codes have (each ≥ 10% of the brand's codes), most common first. */
  tipiche: FormaTipica[]
  /** Every shape seen often enough not to warn about it. */
  accettate: string[]
  /** Length of the longest accepted `chiaveCodice`. */
  lunghezzaMax: number
}

/** Below this many codes a brand's shapes say nothing: no profile, no example. */
export const MIN_CODICI_PROFILO = 10
const QUOTA_TIPICA = 0.1
const MAX_TIPICHE = 3
/** A shape is accepted when ≥ 2 codes and ≥ 0.5% of the brand's codes have it. */
const QUOTA_ACCETTATA = 0.005

export type CodiceEsistente = { codice: string; nOrdiniStorici?: number | null }

/**
 * The brand's profile from its codes, or `null` when it has fewer than `MIN_CODICI_PROFILO`.
 * Codes are counted once per paint; the example of a shape is its most ordered code.
 */
export const profiloCodici = (marca: string, codici: CodiceEsistente[]): ProfiloCodice | null => {
  const perForma = new Map<string, { n: number; esempio: CodiceEsistente }>()
  for (const riga of codici) {
    const forma = formaCodice(riga.codice)
    if (!forma) continue
    const voce = perForma.get(forma)
    if (!voce) perForma.set(forma, { esempio: riga, n: 1 })
    else {
      voce.n++
      if ((riga.nOrdiniStorici ?? 0) > (voce.esempio.nOrdiniStorici ?? 0)) voce.esempio = riga
    }
  }
  const totale = [...perForma.values()].reduce((s, v) => s + v.n, 0)
  if (totale < MIN_CODICI_PROFILO) return null

  const ordinate = [...perForma.entries()].sort((a, b) => b[1].n - a[1].n)
  const tipiche = ordinate.filter(([, v]) => v.n / totale >= QUOTA_TIPICA).slice(0, MAX_TIPICHE)
  const accettate = ordinate
    .filter(([, v]) => v.n >= 2 && v.n / totale >= QUOTA_ACCETTATA)
    .map(([forma]) => forma)

  return {
    accettate,
    lunghezzaMax: Math.max(...accettate.map((f) => f.length)),
    marca,
    // A brand without a dominant shape still gets its most common one as the example.
    tipiche: (tipiche.length ? tipiche : ordinate.slice(0, 1)).map(([forma, v]) => ({
      esempio: v.esempio.codice.trim(),
      forma,
    })),
  }
}

const NUMERI = ['', 'una', 'due', 'tre', 'quattro', 'cinque', 'sei', 'sette', 'otto', 'nove']

const elenco = (parti: string[], congiunzione: string) =>
  parti.length <= 1
    ? (parti[0] ?? '')
    : `${parti.slice(0, -1).join(', ')} ${congiunzione} ${parti[parti.length - 1]}`

/** `999L` → "tre cifre e una lettera", `999/9` → "tre cifre, una barra e una cifra". */
export const descriviForma = (forma: string): string =>
  elenco(
    (forma.match(/9+|L+|\/+/g) ?? []).map((gruppo) => {
      const n = gruppo.length
      const quanti = NUMERI[n] ?? String(n)
      if (gruppo[0] === '/') return 'una barra'
      if (gruppo[0] === '9') return `${quanti} ${n === 1 ? 'cifra' : 'cifre'}`
      return `${quanti} ${n === 1 ? 'lettera' : 'lettere'}`
    }),
    'e',
  )

/** "tre cifre e una lettera (es. 296/A) o tre cifre (es. 647)": the first two typical shapes. */
export const descriviTipiche = (profilo: ProfiloCodice): string =>
  elenco(
    profilo.tipiche.slice(0, 2).map((t) => `${descriviForma(t.forma)} (es. ${t.esempio})`),
    'o',
  )

/** The hint under the code field. */
export const suggerimentoCodice = (profilo: ProfiloCodice): string =>
  `I codici ${profilo.marca} di solito hanno ${descriviTipiche(profilo)}.`

/**
 * A vehicle identification number: 17 letters and digits, never I, O or Q. Italian booklets
 * print it as field E, and customers often copy it instead of the paint code.
 */
export const sembraTelaio = (chiave: string): boolean =>
  /^[A-HJ-NPR-Z0-9]{17}$/.test(chiave) && /\d/.test(chiave) && /[A-Z]/.test(chiave)

/**
 * A gentle warning when `testo` doesn't look like one of the brand's codes, else `null`. It
 * never blocks the search: the code may just be rare. Without a profile only the checks that
 * don't depend on the brand run (chassis number, no letters or digits, very long text).
 */
export const avvisoCodice = (testo: string, profilo: ProfiloCodice | null): string | null => {
  if (!testo.trim()) return null
  const chiave = chiaveCodice(testo)
  const forma = formaCodice(testo)
  const tipiche = profilo
    ? ` I codici ${profilo.marca} di solito hanno ${descriviTipiche(profilo)}.`
    : ''

  if (!chiave) return 'Il codice colore è fatto di lettere e cifre: controlla cosa hai scritto.'
  if (sembraTelaio(chiave))
    return `Sembra il numero di telaio (17 caratteri), non il codice colore.${tipiche}`
  if (profilo?.accettate.includes(forma)) return null
  // Only letters, and a colour word ("rosso") or longer than any code: probably the name.
  const soloLettere = /^L+$/.test(forma)
  if (
    soloLettere &&
    (famigliaDaNome(testo) ||
      (forma.length >= 5 && (!profilo || forma.length > profilo.lunghezzaMax)))
  )
    return `Sembra il nome di un colore. Lascia vuoto il codice per vedere le tinte del modello, oppure usa Cerca nel sito.${tipiche}`
  if (chiave.length > (profilo ? profilo.lunghezzaMax + 2 : 12))
    return `È più lungo di un codice colore: forse è il telaio o il codice di un ricambio.${tipiche}`
  if (!profilo) return null
  return `Non sembra un codice ${profilo.marca}.${tipiche} Controlla la targhetta: puoi comunque cercare.`
}
